/**
 * WhatsappManagement ("WhatsApp") — route /whatsapp.
 *
 * WhatsApp sends from Tal's personal number through whatsapp-web.js on the server. This screen:
 *   1. Connection — status, phone, reconnect, and the QR code to scan when the session is new.
 *   2. Send queue  — counts per job status, pause / resume, when sending happens.
 *   3. Settings    — random delay between messages, sending hours, pacing (pause after N messages).
 *                    PUT /api/whatsapp/config; the server saves them to whatsapp-config.json, so they
 *                    survive a restart (the .env values are only the first-run defaults).
 *   4. History     — server-paged list of queue jobs (50 per page), filtered by status.
 *
 * Data (unchanged): getWhatsappStatus (every 15s; every 2s for up to a minute after "reconnect",
 * until a QR / ready / failure shows up) · getWhatsappQueue + getWhatsappQueueStats (every 10s) ·
 * initializeWhatsapp · pause/resumeWhatsappQueue · updateWhatsappConfig. Messages via showAlert.
 *
 * UI-only behavior change: the 15s status poll no longer overwrites the settings fields while the
 * user is editing them (it used to reset half-typed values). After a save they follow the server again.
 */
import React, { useState, useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  ReloadOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  MobileOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

import * as actionsCreators from '../../actions/index.js';
import {
  Page,
  PageHeader,
  Section,
  DataTable,
  EmptyState,
  StatusBadge,
  StatCard,
  Metric,
  MetricRow,
  StatusDot,
  FormGrid,
  Field,
  Button,
  InputNumber,
  Segmented,
  Tooltip,
  useBreakpoint,
} from '../../ui';
import classes from './WhatsappManagement.css';

// Connection states from the server (whatsapp-web.js) → label + dot color
const STATUS = {
  disconnected: { label: 'מנותק', color: '#DC2626' },
  qr_ready: { label: 'ממתין לסריקה', color: '#D97706' },
  authenticated: { label: 'מאמת…', color: '#D97706' },
  ready: { label: 'מחובר', color: '#16A34A' },
  auth_failure: { label: 'שגיאת אימות', color: '#DC2626' },
};

// Queue job states → label + color
const JOB = {
  pending: { label: 'ממתין', color: '#2563EB' },
  sending: { label: 'שולח…', color: '#7C3AED' },
  sent: { label: 'נשלח', color: '#16A34A' },
  failed: { label: 'נכשל', color: '#DC2626' },
  skipped: { label: 'דולג', color: '#A1A1AA' },
};

const FILTERS = [
  { key: 'all', label: 'הכל' },
  { key: 'pending', label: 'ממתין' },
  { key: 'sent', label: 'נשלח' },
  { key: 'failed', label: 'נכשל' },
];

const PAGE_SIZE = 50; // fixed on the server (routes/whatsapp.route.js)
const hh = (h) => `${String(h).padStart(2, '0')}:00`;

// Current hour in Israel — the worker checks the window in WHATSAPP_TIMEZONE (default Asia/Jerusalem).
const israelHour = () => {
  try {
    return parseInt(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone: 'Asia/Jerusalem' }).format(new Date()), 10);
  } catch (e) {
    return new Date().getHours();
  }
};

const WhatsappManagement = (props) => {
  const [waStatus, setWaStatus] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueStats, setQueueStats] = useState({});
  const [queuePage, setQueuePage] = useState(1);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queueLoading, setQueueLoading] = useState(true); // UI only: skeleton on first load
  const [statusFilter, setStatusFilter] = useState('all');
  const [delayMin, setDelayMin] = useState(20);
  const [delayMax, setDelayMax] = useState(60);
  const [hoursStart, setHoursStart] = useState(9);
  const [hoursEnd, setHoursEnd] = useState(20);
  const [batchSize, setBatchSize] = useState(0);
  const [batchPauseMinutes, setBatchPauseMinutes] = useState(5);
  const [configDirty, setConfigDirty] = useState(false); // UI only: fields edited, not saved yet
  const [configSaving, setConfigSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const pollRef = useRef(null);
  const queuePollRef = useRef(null);
  const fastPollRef = useRef(null);
  const statusFilterRef = useRef(statusFilter);
  const queuePageRef = useRef(queuePage);
  const configDirtyRef = useRef(false); // read inside the poll's closure
  const screens = useBreakpoint();

  const fillConfig = (config) => {
    setDelayMin(Math.round(config.delayMinMs / 1000));
    setDelayMax(Math.round(config.delayMaxMs / 1000));
    setHoursStart(config.sendHoursStart);
    setHoursEnd(config.sendHoursEnd);
    setBatchSize(config.batchSize || 0);
    setBatchPauseMinutes(Math.round((config.batchPauseMs || 300000) / 60000));
  };

  const fetchStatus = async () => {
    try {
      const data = await props.getWhatsappStatus();
      setWaStatus(data);
      // Don't overwrite what the user is typing
      if (data.config && !configDirtyRef.current) fillConfig(data.config);
      return data;
    } catch (e) {
      console.error('WhatsApp status error:', e);
    }
  };

  const startFastPoll = () => {
    if (fastPollRef.current) clearInterval(fastPollRef.current);
    fastPollRef.current = setInterval(async () => {
      const data = await fetchStatus();
      if (data && (data.status === 'qr_ready' || data.status === 'ready' || data.status === 'auth_failure')) {
        clearInterval(fastPollRef.current);
        fastPollRef.current = null;
      }
    }, 2000);
    setTimeout(() => {
      if (fastPollRef.current) {
        clearInterval(fastPollRef.current);
        fastPollRef.current = null;
      }
    }, 60000);
  };

  const fetchQueue = async (page = queuePage, filter = statusFilter) => {
    try {
      const params = { page };
      if (filter !== 'all') params.status = filter;
      const data = await props.getWhatsappQueue(params);
      setQueue(data.jobs || []);
      setQueueTotal(data.total || 0);
    } catch (e) {
      console.error('WhatsApp queue error:', e);
    }
    setQueueLoading(false);
  };

  const fetchStats = async () => {
    try {
      const data = await props.getWhatsappQueueStats();
      setQueueStats(data);
    } catch (e) {
      console.error('WhatsApp stats error:', e);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchQueue(1, 'all');
    fetchStats();

    pollRef.current = setInterval(fetchStatus, 15000);
    queuePollRef.current = setInterval(() => {
      fetchQueue(queuePageRef.current, statusFilterRef.current);
      fetchStats();
    }, 10000);

    return () => {
      clearInterval(pollRef.current);
      clearInterval(queuePollRef.current);
      if (fastPollRef.current) clearInterval(fastPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (filter) => {
    statusFilterRef.current = filter;
    setStatusFilter(filter);
    setQueuePage(1);
    queuePageRef.current = 1;
    fetchQueue(1, filter);
  };

  const handlePageChange = (page) => {
    queuePageRef.current = page;
    setQueuePage(page);
    fetchQueue(page, statusFilterRef.current);
  };

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      await props.initializeWhatsapp();
      startFastPoll();
    } catch (e) {
      props.showAlert('שגיאה בחיבור WhatsApp');
    }
    setInitializing(false);
  };

  const handlePause = async () => {
    try {
      await props.pauseWhatsappQueue();
      await fetchStatus();
    } catch (e) {
      props.showAlert('שגיאה בעצירת תור');
    }
  };

  const handleResume = async () => {
    try {
      await props.resumeWhatsappQueue();
      await fetchStatus();
    } catch (e) {
      props.showAlert('שגיאה בהמשך תור');
    }
  };

  // Every settings field goes through here: set the value + mark the form as edited
  const edit = (setter, fallback) => (value) => {
    configDirtyRef.current = true;
    setConfigDirty(true);
    setter(value === null || value === undefined || Number.isNaN(value) ? fallback : value);
  };

  const handleResetConfig = () => {
    configDirtyRef.current = false;
    setConfigDirty(false);
    if (waStatus && waStatus.config) fillConfig(waStatus.config);
  };

  const handleSaveConfig = async () => {
    if (delayMin >= delayMax) {
      props.showAlert('מינימום שניות חייב להיות קטן ממקסימום');
      return;
    }
    if (hoursStart >= hoursEnd) {
      props.showAlert('שעת התחלה חייבת להיות קטנה משעת סיום');
      return;
    }
    setConfigSaving(true);
    try {
      await props.updateWhatsappConfig({
        delayMinMs: delayMin * 1000,
        delayMaxMs: delayMax * 1000,
        sendHoursStart: hoursStart,
        sendHoursEnd: hoursEnd,
        batchSize: batchSize,
        batchPauseMs: batchPauseMinutes * 60000,
      });
      props.showAlert('הגדרות נשמרו');
      configDirtyRef.current = false;
      setConfigDirty(false);
      fetchStatus(); // the queue card shows the saved values
    } catch (e) {
      props.showAlert((e.response && e.response.data && e.response.data.error) || 'שגיאה בשמירת הגדרות');
    }
    setConfigSaving(false);
  };

  const currentStatus = (waStatus && waStatus.status) || 'disconnected';
  const statusMeta = STATUS[currentStatus] || { label: currentStatus, color: '#A1A1AA' };
  const isPaused = waStatus && waStatus.isPaused;
  const saved = (waStatus && waStatus.config) || null; // what the worker uses right now
  const outsideHours = saved && (israelHour() < saved.sendHoursStart || israelHour() >= saved.sendHoursEnd);
  const pending = queueStats.pending || 0;

  // One line under the queue state: why messages are / aren't going out right now
  let queueLine = null;
  if (isPaused) queueLine = pending ? `${pending} הודעות ממתינות ויישלחו כשתמשיכו את התור.` : 'שום הודעה לא תישלח עד שתמשיכו את התור.';
  else if (currentStatus !== 'ready') queueLine = 'WhatsApp לא מחובר — ההודעות יחכו בתור עד שיתחבר.';
  else if (outsideHours) queueLine = `מחוץ לשעות השליחה — השליחה תמשיך ב-${hh(saved.sendHoursStart)}.`;
  else if (pending) queueLine = `שולח עכשיו: ${pending} הודעות בתור.`;

  const columns = [
    {
      key: 'customer',
      title: 'לקוח',
      width: screens.md === false ? 140 : 180,
      render: (_, job) => {
        const name = job.customer ? `${job.customer.firstname || ''} ${job.customer.lastname || ''}`.trim() : '';
        return (
          <div className={classes.customerCell}>
            <span className={classes.customerName}>{name || '—'}</span>
            {/* phones: the phone column is hidden, so show the number here */}
            {screens.md === false ? (
              <span className={classes.phoneSmall} dir="ltr">
                {job.phone}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'phone',
      title: 'טלפון',
      width: 140,
      responsive: ['md'],
      render: (_, job) => (
        <span className={classes.phone} dir="ltr">
          {job.phone}
        </span>
      ),
    },
    {
      key: 'message',
      title: 'הודעה',
      render: (_, job) => (
        <Tooltip title={<span className={classes.messageTip}>{job.message}</span>} placement="topRight" mouseEnterDelay={0.4}>
          <span className={classes.message}>{job.message}</span>
        </Tooltip>
      ),
    },
    {
      key: 'status',
      title: 'סטטוס',
      width: 120,
      render: (_, job) => {
        const meta = JOB[job.status] || { label: job.status, color: '#A1A1AA' };
        return (
          <span className={classes.statusCell}>
            <StatusBadge color={meta.color}>{meta.label}</StatusBadge>
            {job.error_message ? (
              <Tooltip title={job.error_message}>
                <ExclamationCircleOutlined className={classes.errorIcon} aria-label="פרטי השגיאה" />
              </Tooltip>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'sent_at',
      title: 'נשלח',
      width: 160,
      responsive: ['sm'],
      render: (_, job) =>
        job.sent_at ? (
          <span className={classes.date}>{new Date(job.sent_at).toLocaleString('he-IL')}</span>
        ) : (
          <span className={classes.muted}>—</span>
        ),
    },
  ];

  return (
    <Page>
      <PageHeader title="WhatsApp" subtitle="שליחת הודעות ללקוחות מהמספר האישי" />

      <div className={classes.warning} role="note">
        <WarningOutlined />
        <span>
          <strong>שימו לב:</strong> השליחה מתבצעת מהמספר האישי שלך. שליחת כמויות גדולות עלולה לגרום לחסימת החשבון ע"י WhatsApp.
        </span>
      </div>

      <div className={classes.topGrid}>
        {/* ---- 1. connection ---- */}
        <StatCard
          title="חיבור"
          extra={
            <Button size="small" icon={<ReloadOutlined />} loading={initializing} onClick={handleInitialize}>
              {initializing ? 'מתחבר…' : 'התחבר מחדש'}
            </Button>
          }
        >
          <div className={classes.connection}>
            <span className={classes.connectionState}>
              <StatusDot color={statusMeta.color}>{statusMeta.label}</StatusDot>
            </span>
            {waStatus && waStatus.phone ? (
              <span className={classes.connectionPhone}>
                <MobileOutlined />
                <span dir="ltr">+{waStatus.phone}</span>
              </span>
            ) : null}
          </div>

          {currentStatus === 'qr_ready' && waStatus && waStatus.qrDataUri ? (
            <div className={classes.qr}>
              <img src={waStatus.qrDataUri} alt="קוד QR להתחברות WhatsApp" width={200} height={200} className={classes.qrImage} />
              <ol className={classes.qrSteps}>
                <li>פתחו WhatsApp בטלפון</li>
                <li>הגדרות ← מכשירים מקושרים</li>
                <li>קישור מכשיר, וסרקו את הקוד</li>
              </ol>
            </div>
          ) : null}

          {currentStatus === 'authenticated' ? (
            <p className={classes.connecting}>
              <LoadingOutlined /> מאמת, אנא המתן…
            </p>
          ) : null}

          {(currentStatus === 'disconnected' || currentStatus === 'auth_failure') && !initializing ? (
            <p className={classes.hint}>לחצו "התחבר מחדש" כדי לקבל קוד QR לסריקה.</p>
          ) : null}
        </StatCard>

        {/* ---- 2. queue ---- */}
        <StatCard
          title="תור שליחה"
          extra={
            isPaused ? (
              <Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={handleResume}>
                המשך
              </Button>
            ) : (
              <Button size="small" icon={<PauseCircleOutlined />} onClick={handlePause}>
                עצור
              </Button>
            )
          }
        >
          <div className={classes.queueState}>
            <StatusDot color={isPaused ? '#D97706' : '#16A34A'}>{isPaused ? 'מושהה' : 'פעיל'}</StatusDot>
            {saved ? (
              <span className={classes.queueWindow}>
                שעות שליחה {hh(saved.sendHoursStart)}–{hh(saved.sendHoursEnd)}
              </span>
            ) : null}
          </div>
          {queueLine ? <p className={classes.queueLine}>{queueLine}</p> : null}
          <MetricRow>
            {['pending', 'sending', 'sent', 'failed', 'skipped'].map((s) => (
              <Metric key={s} value={queueStats[s] || 0} label={JOB[s].label} color={queueStats[s] ? JOB[s].color : undefined} />
            ))}
          </MetricRow>
        </StatCard>
      </div>

      {/* ---- 3. settings ---- */}
      <Section
        title="הגדרות שליחה"
        extra={
          <div className={classes.settingsActions}>
            {configDirty ? (
              <Button size="small" type="text" onClick={handleResetConfig} disabled={configSaving}>
                ביטול
              </Button>
            ) : null}
            <Button size="small" type="primary" onClick={handleSaveConfig} loading={configSaving} disabled={!configDirty}>
              שמור
            </Button>
          </div>
        }
      >
        <div className={classes.settings}>
          <div className={classes.group}>
            <div className={classes.groupTitle}>השהייה בין הודעות</div>
            <FormGrid>
              <Field label="מינימום">
                <InputNumber min={5} max={300} value={delayMin} onChange={edit(setDelayMin, 20)} addonAfter="שניות" />
              </Field>
              <Field label="מקסימום">
                <InputNumber min={5} max={600} value={delayMax} onChange={edit(setDelayMax, 60)} addonAfter="שניות" />
              </Field>
            </FormGrid>
          </div>
          <div className={classes.group}>
            <div className={classes.groupTitle}>שעות שליחה (שעון ישראל)</div>
            <FormGrid>
              <Field label="מ-">
                <InputNumber min={0} max={22} value={hoursStart} onChange={edit(setHoursStart, 0)} formatter={(v) => (v === '' || v === undefined ? '' : hh(v))} parser={(v) => parseInt(v, 10)} />
              </Field>
              <Field label="עד">
                <InputNumber min={1} max={23} value={hoursEnd} onChange={edit(setHoursEnd, 20)} formatter={(v) => (v === '' || v === undefined ? '' : hh(v))} parser={(v) => parseInt(v, 10)} />
              </Field>
            </FormGrid>
          </div>
          <div className={classes.group}>
            <div className={classes.groupTitle}>הפסקה אחרי כל קבוצה</div>
            <FormGrid>
              <Field label="כל כמה הודעות" hint="0 = בלי הפסקות">
                <InputNumber min={0} value={batchSize} onChange={edit(setBatchSize, 0)} addonAfter="הודעות" />
              </Field>
              <Field label="אורך ההפסקה">
                <InputNumber min={1} value={batchPauseMinutes} onChange={edit(setBatchPauseMinutes, 5)} disabled={batchSize === 0} addonAfter="דקות" />
              </Field>
            </FormGrid>
          </div>
        </div>
        <p className={classes.summary}>
          כל הודעה יוצאת {delayMin}–{delayMax} שניות אחרי הקודמת, בין {hh(hoursStart)} ל-{hh(hoursEnd)}
          {batchSize > 0 ? `, ואחרי כל ${batchSize} הודעות יש הפסקה של ${batchPauseMinutes} דקות` : ''}.
          {' '}נשמר בשרת וממשיך לחול גם אחרי הפעלה מחדש.
        </p>
      </Section>

      {/* ---- 4. history ---- */}
      <Section
        title="היסטוריית שליחה"
        padded={false}
        extra={
          <Segmented
            size="small"
            value={statusFilter}
            onChange={handleFilterChange}
            options={FILTERS.map((f) => ({
              value: f.key,
              label: f.key !== 'all' && queueStats[f.key] ? `${f.label} · ${queueStats[f.key]}` : f.label,
            }))}
          />
        }
      >
        <DataTable
          data={queue}
          loading={queueLoading}
          fit
          columns={columns}
          empty={<EmptyState title="אין הודעות להצגה" description={statusFilter === 'all' ? 'הודעות שנשלחות מרשימת הלקוחות יופיעו כאן' : undefined} />}
          pagination={{
            current: queuePage,
            total: queueTotal,
            pageSize: PAGE_SIZE,
            showSizeChanger: false,
            onChange: handlePageChange,
          }}
        />
      </Section>
    </Page>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getWhatsappStatus: actionsCreators.getWhatsappStatus,
      initializeWhatsapp: actionsCreators.initializeWhatsapp,
      getWhatsappQueue: actionsCreators.getWhatsappQueue,
      getWhatsappQueueStats: actionsCreators.getWhatsappQueueStats,
      pauseWhatsappQueue: actionsCreators.pauseWhatsappQueue,
      resumeWhatsappQueue: actionsCreators.resumeWhatsappQueue,
      updateWhatsappConfig: actionsCreators.updateWhatsappConfig,
      showAlert: actionsCreators.showAlert,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(WhatsappManagement);
